import { toast } from "@devhop/ui";
import {
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  CreateQCStandardFormDTO,
  UpdateQCStandardFormDTO,
} from "@/modules/qc/domain/contracts";
import type {
  CreateQCRecordDTO,
  UpdateQCRecordDTO,
} from "@/modules/qc/domain/contracts/record";
import type { QCResultStatsQueryDTO } from "@/modules/qc/domain/contracts/result";
import type {
  CreateWorkItemTypeDTO,
  UpdateWorkItemTypeDTO,
} from "@/modules/qc/domain/contracts/work-item-type";
import type {
  CreateQCWorkTypeDTO,
  UpdateQCWorkTypeDTO,
} from "@/modules/qc/domain/contracts/work-type";
import type {
  QCStandardByIdResult,
  QCStandardsListResult,
} from "@/modules/qc/domain/types";
import type {
  QCRecordByIdResult,
  QCRecordsListResult,
} from "@/modules/qc/domain/types/record";
import type { QCResultStatsResult } from "@/modules/qc/domain/types/result";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import {
  qcApi,
  qcRecordApi,
  qcResultApi,
  qcWorkItemTypesApi,
  qcWorkTypesApi,
} from "./client";

export const qcKeys = {
  all: ["qc"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["qc", "list", q] as const,
  detail: (id: string) => ["qc", "detail", id] as const,
};

export const qcWorkTypeKeys = {
  all: ["qc-work-types"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["qc-work-types", "list", q] as const,
  detail: (id: string) => ["qc-work-types", "detail", id] as const,
};

export const qcWorkItemTypeKeys = {
  all: ["qc-work-item-types"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["qc-work-item-types", "list", q] as const,
  detail: (id: string) => ["qc-work-item-types", "detail", id] as const,
};

export function useQCStandardsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery<QCStandardsListResult>({
    queryKey: qcKeys.list(q),
    queryFn: () => qcApi.list(q),
  });
}

export function useQCStandardQuery(id: string) {
  return useQuery<QCStandardByIdResult>({
    queryKey: qcKeys.detail(id),
    queryFn: () => qcApi.get(id),
    enabled: !!id,
  });
}

export function useCreateQCStandard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQCStandardFormDTO) => qcApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງມາດຕະຖານ QC ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: qcKeys.all });
    },
    onError: () => toast.error("ສ້າງມາດຕະຖານ QC ລົ້ມເຫຼວ"),
  });
}

export function useUpdateQCStandard(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateQCStandardFormDTO) => qcApi.update(id, input),
    onSuccess: () => {
      toast.success("ແກ້ໄຂມາດຕະຖານ QC ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: qcKeys.detail(id) });
      qc.invalidateQueries({ queryKey: qcKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂມາດຕະຖານ QC ລົ້ມເຫຼວ"),
  });
}

export function useDeleteQCStandard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => qcApi.delete(id),
    onSuccess: () => {
      toast.success("ລຶບມາດຕະຖານ QC ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: qcKeys.all });
    },
    onError: () => toast.error("ລຶບມາດຕະຖານ QC ລົ້ມເຫຼວ"),
  });
}

export function useQCWorkTypes(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: qcWorkTypeKeys.list(query),
    queryFn: () => qcWorkTypesApi.list(query),
  });
}

export function useQCWorkType(id: string) {
  return useQuery({
    queryKey: qcWorkTypeKeys.detail(id),
    queryFn: () => qcWorkTypesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateQCWorkType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQCWorkTypeDTO) => qcWorkTypesApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງປະເພດງານ QC ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: qcWorkTypeKeys.all });
    },
    onError: () => toast.error("ສ້າງປະເພດງານ QC ລົ້ມເຫຼວ"),
  });
}

export function useUpdateQCWorkType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateQCWorkTypeDTO }) =>
      qcWorkTypesApi.update(id, input),
    onSuccess: (_, { id }) => {
      toast.success("ແກ້ໄຂປະເພດງານ QC ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: qcWorkTypeKeys.detail(id) });
      qc.invalidateQueries({ queryKey: qcWorkTypeKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂປະເພດງານ QC ລົ້ມເຫຼວ"),
  });
}

export function useDeleteQCWorkType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => qcWorkTypesApi.delete(id),
    onSuccess: () => {
      toast.success("ລຶບປະເພດງານ QC ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: qcWorkTypeKeys.all });
    },
    onError: () => toast.error("ລຶບປະເພດງານ QC ລົ້ມເຫຼວ"),
  });
}

// Work Item Type queries
export function useWorkItemTypes(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: qcWorkItemTypeKeys.list(query),
    queryFn: () => qcWorkItemTypesApi.list(query),
  });
}

export function useWorkItemType(id: string) {
  return useQuery({
    queryKey: qcWorkItemTypeKeys.detail(id),
    queryFn: () => qcWorkItemTypesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateWorkItemType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkItemTypeDTO) =>
      qcWorkItemTypesApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງປະເພດງານທີ່ກວດສອບ ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: qcWorkItemTypeKeys.all });
    },
    onError: () => toast.error("ສ້າງປະເພດງານທີ່ກວດສອບ ລົ້ມເຫຼວ"),
  });
}

export function useUpdateWorkItemType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateWorkItemTypeDTO }) =>
      qcWorkItemTypesApi.update(id, input),
    onSuccess: (_, { id }) => {
      toast.success("ແກ້ໄຂປະເພດງານທີ່ກວດສອບ ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: qcWorkItemTypeKeys.detail(id) });
      qc.invalidateQueries({ queryKey: qcWorkItemTypeKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂປະເພດງານທີ່ກວດສອບ ລົ້ມເຫຼວ"),
  });
}

export function useDeleteWorkItemType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => qcWorkItemTypesApi.delete(id),
    onSuccess: () => {
      toast.success("ລຶບປະເພດງານທີ່ກວດສອບ ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: qcWorkItemTypeKeys.all });
    },
    onError: () => toast.error("ລຶບປະເພດງານທີ່ກວດສອບ ລົ້ມເຫຼວ"),
  });
}

// QC Record queries
export const qcRecordKeys = {
  all: ["qc-record"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["qc-record", "list", q] as const,
  detail: (id: string) => ["qc-record", "detail", id] as const,
};

export const qcResultKeys = {
  all: ["qc-result"] as const,
  stats: (q: Partial<QCResultStatsQueryDTO>) =>
    ["qc-result", "stats", q] as const,
};

export function useQCRecordsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery<QCRecordsListResult>({
    queryKey: qcRecordKeys.list(q),
    queryFn: () => qcRecordApi.list(q),
  });
}

export function useQCRecordQuery(
  id: string,
): UseQueryResult<QCRecordByIdResult> {
  return useQuery<QCRecordByIdResult>({
    queryKey: qcRecordKeys.detail(id),
    queryFn: () => qcRecordApi.get(id),
    enabled: !!id,
  });
}

export function useCreateQCRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQCRecordDTO) => qcRecordApi.create(input),
    onSuccess: () => {
      toast.success("ບັນທຶກຜົນກວດ QC ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: qcRecordKeys.all });
      qc.invalidateQueries({ queryKey: qcResultKeys.all });
    },
    onError: () => toast.error("ບັນທຶກຜົນກວດ QC ລົ້ມເຫຼວ"),
  });
}

export function useUpdateQCRecord(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateQCRecordDTO) => qcRecordApi.update(id, input),
    onSuccess: () => {
      toast.success("ແກ້ໄຂຜົນກວດ QC ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: qcRecordKeys.detail(id) });
      qc.invalidateQueries({ queryKey: qcRecordKeys.all });
      qc.invalidateQueries({ queryKey: qcResultKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂຜົນກວດ QC ລົ້ມເຫຼວ"),
  });
}

export function useDeleteQCRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => qcRecordApi.delete(id),
    onSuccess: () => {
      toast.success("ລຶບຜົນກວດ QC ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: qcRecordKeys.all });
      qc.invalidateQueries({ queryKey: qcResultKeys.all });
    },
    onError: () => toast.error("ລຶບຜົນກວດ QC ລົ້ມເຫຼວ"),
  });
}

export function useQCResultStats(
  query: Partial<QCResultStatsQueryDTO> = {},
  options?: { enabled?: boolean },
) {
  return useQuery<QCResultStatsResult>({
    queryKey: qcResultKeys.stats(query),
    queryFn: () => qcResultApi.getStats(query),
    enabled: options?.enabled ?? true,
  });
}
